/* eslint-disable no-console */
import {
  PNS_TOPIC_BY_ENV,
  authState$,
  createAuthState$,
  createDetailedAuthState$,
  detailedAuthState$,
  logIn,
  logOut,
  optOut,
  type PnsSub,
  flog,
} from '@heimdall/client';
import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Subscription } from 'rxjs';
import { idpLookupUrls } from '../config.js';
import { rigSharedStyles } from './heimdall-rig-styles.js';

// WS-specific env → issuerUrl mapping (moved from SDK)
const ENVS = ['dev', 'qa', 'ppe', 'prod'] as const;
type Env = (typeof ENVS)[number];

const issuerUrls: Record<Env, string> = {
  dev: 'https://token.dev-workspace.t-lseg.com/auth/v1',
  qa: 'https://token.qa-workspace.t-lseg.com/auth/v1',
  ppe: 'https://token.ppe-workspace.t-lseg.com/auth/v1',
  prod: 'https://token.workspace.lseg.com/auth/v1',
};

const clientUids = ['workspace', 'teams_test_local', 'sdk'];

const presets = {
  'workspace-popup': {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'workspace',
    redirectUri: `${globalThis.location.origin}/public/heimdall/redirect/index.html`,
    fallback: 'none',
    preferred: 'popup',
    optimistic: false,
  },
  'workspace-ppe': {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'workspace',
    redirectUri: `${globalThis.location.origin}/public/heimdall/redirect/index.html`,
    fallback: 'popup',
    optimistic: true,
  },
  'third-party sdk': {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'sdk',
    redirectUri: 'https://thirdparty.local:8081/redirect.html',
    clientId: '934ff280-b6e2-4008-b098-620e017835fd',
    remoteLogin: true,
  },
  'excel-add-in': {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'excel_addin',
    remoteLogin: true,
  },
  teams_test_local: {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'teams_test_local',
  },
  'ping-idp': {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'workspace',
    idp: 'ping',
    redirectUri: `${globalThis.location.origin}/public/heimdall/redirect/index.html`,
    fallback: 'popup',
    optimistic: false,
  },
  'ping-remote': {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'workspace',
    idp: 'ping',
    remoteLogin: true,
    redirectUri: `${globalThis.location.origin}/public/heimdall/redirect/index.html`,
    fallback: 'popup',
    optimistic: false,
  },
  'lseg-identity-ping': {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'lseg_identity',
    idp: 'ping',
    redirectUri: `${globalThis.location.origin}/public/heimdall/redirect/index.html`,
    fallback: 'popup',
    optimistic: false,
  },
  'lseg-identity-entra': {
    issuerUrl: issuerUrls['ppe'],
    clientUid: 'lseg_identity',
    idp: 'entra',
    redirectUri: `${globalThis.location.origin}/public/heimdall/redirect/index.html`,
    fallback: 'popup',
    optimistic: false,
  },
};

@customElement('heimdall-rig-auth-state')
export class HeimdallRigAuthState extends LitElement {
  @state()
  _redirectUri: string | undefined = presets['workspace-ppe'].redirectUri;

  @state()
  _clientUid = clientUids[0];
  @state()
  _clientId: string | undefined;

  @state()
  _idp: string | undefined;

  @state()
  _userId: string | undefined;

  @state()
  _idpLookupUrl: string = idpLookupUrls['ppe'];

  @state()
  _authState = '×';

  @state()
  _detailedAuthState: { authState: string; reason?: unknown } | null = null;

  @state()
  _issuerUrl: string = issuerUrls['ppe'];

  @state()
  _remoteLogin = false;

  @state()
  _persistTokens = false;

  @state()
  _result = '';

  @state()
  _fallback = 'popup';

  @state()
  _preferred = '';

  @state()
  _subscription: Subscription | null = null;

  @state()
  _isSubscribed = false;

  @state()
  _optimistic: boolean | undefined;

  @state()
  _redirectRoot = `${globalThis.location.origin}/public/heimdall/index.html`;

  @state()
  _redirectNextPath = '/public/heimdall/audience/index.html';

  @state()
  _linkUrl = `${globalThis.location.origin}/public/heimdall/link.html`;

  @state()
  _lane: string | undefined;

  @state()
  _pnsEnv: Env | 'disabled' = 'ppe';

  @state()
  _pnsTopic = PNS_TOPIC_BY_ENV['ppe'];

  onPresetSelected(presetKey: keyof typeof presets) {
    const preset = presets[presetKey];

    this._clientUid = preset.clientUid;
    this._issuerUrl = preset.issuerUrl;

    this._redirectUri =
      'redirectUri' in preset ? preset.redirectUri : undefined;

    this._clientId = 'clientId' in preset ? preset.clientId : undefined;
    this._idp = 'idp' in preset ? preset.idp : undefined;
    this._remoteLogin = 'remoteLogin' in preset ? preset.remoteLogin : false;
    this._fallback = 'fallback' in preset ? preset.fallback : '';
    this._optimistic = 'optimistic' in preset ? preset.optimistic : undefined;
    this._preferred = 'preferred' in preset ? (preset.preferred as string) : '';
  }

  onSubscriptionRequestChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const { checked } = target;

    if (!checked) {
      this._authState = '×';
      this._detailedAuthState = null;
      this._isSubscribed = false;
      return this._subscription?.unsubscribe();
    }

    this._isSubscribed = true;
    this._subscription = new Subscription();

    const authStateStream$ = this._lane
      ? createAuthState$(this._lane)
      : authState$;

    const detailedAuthStateStream$ = this._lane
      ? createDetailedAuthState$(this._lane)
      : detailedAuthState$;

    const laneLabel = this._lane ? ` [${this._lane}]` : '';

    this._subscription.add(
      authStateStream$
        .pipe(
          flog(
            `Auth state on RIG${laneLabel}`,
            'background: pink; color: navy; padding: 5px;'
          )
        )
        .subscribe((authState: string) => {
          this._authState = authState;
        })
    );

    this._subscription.add(
      detailedAuthStateStream$
        .pipe(
          flog(
            `Auth state 2 on RIG${laneLabel}`,
            'background: silver; color: black; padding: 5px'
          )
        )
        .subscribe((detailed) => {
          this._detailedAuthState = detailed;
        })
    );
  }

  onRemoteLoginChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const { checked } = target;
    this._remoteLogin = checked;
  }

  onPersistTokensChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const { checked } = target;

    console.log(`%cPERSIST ${checked}`, 'background: pink; padding: 5px;');

    this._persistTokens = checked;
  }

  onOptimisticChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const { checked } = target;
    this._optimistic = checked;
  }

  _getRedirectLoginUrl() {
    const params = new URLSearchParams({
      clientUid: this._clientUid,
      nextPath: this._redirectNextPath || globalThis.location.pathname,
      ...(this._optimistic === false && {
        forceRedirect: 'true',
      }),
      ...(this._persistTokens && { persistTokens: 'true' }),
      ...(this._idp && { idp: this._idp }),
      ...(this._userId && { userId: this._userId }),
      ...(this._lane && { lane: this._lane }),
    });

    const defaultRedirectFrom = new URL(
      `/public/heimdall/index.html?${params.toString()}`,
      globalThis.location.origin
    );

    return this._redirectRoot
      ? `${this._redirectRoot}?${params.toString()}`
      : defaultRedirectFrom.toString();
  }

  async onCopyRedirectLoginUrl() {
    const loginUrl = this._getRedirectLoginUrl();
    await navigator.clipboard.writeText(loginUrl);
    console.log(`Copied to clipboard: ${loginUrl}`);
  }

  onRedirectLogin() {
    const loginUrl = this._getRedirectLoginUrl();

    console.log(
      `%cRedirect login 
      ${loginUrl}
      `,
      'background: pink; color: navy; padding: 5px;'
    );
    globalThis.location.replace(loginUrl);
  }

  async onLogin() {
    this._result = '';
    if (!this._issuerUrl) {
      alert('No issuerUrl set, cannot log in');
      return;
    }

    // Validate userId/idp combination per LoginConfig discriminated union
    if (this._userId && !this._idp && !this._idpLookupUrl) {
      alert(
        'When userId is provided, you must also provide idp or idpLookupUrl'
      );
      return;
    }

    try {
      // Build config based on discriminated union rules
      const baseConfig = {
        issuerUrl: this._issuerUrl,
        clientUid: this._clientUid,
        ...(this._linkUrl && { linkUrl: this._linkUrl }),
        ...(this._fallback && {
          fallback: {
            strategy: this._fallback as 'none' | 'popup' | 'silent',
          },
        }),
        ...(this._preferred &&
          this._preferred === 'popup' && {
            preferred: {
              strategy: this._preferred as 'none' | 'popup' | 'silent',
            },
          }),
        ...(this._redirectUri && { redirectUri: this._redirectUri }),
        ...(this._clientId && { clientId: this._clientId }),
        ...(this._remoteLogin && { remoteLogin: this._remoteLogin }),
        ...(this._optimistic !== undefined && {
          optimistic: this._optimistic,
        }),
        ...(this._persistTokens && { persistTokens: true }),
        ...(this._lane && { lane: this._lane }),
        ...(this._buildPnsSub() && { pnsSub: this._buildPnsSub() }),
      };

      // Type-safe config construction per LoginConfig discriminated union
      let loginConfig;
      if (this._userId && this._idp) {
        loginConfig = { ...baseConfig, userId: this._userId, idp: this._idp };
      } else if (this._userId && this._idpLookupUrl) {
        loginConfig = {
          ...baseConfig,
          userId: this._userId,
          idpLookupUrl: this._idpLookupUrl,
        };
      } else {
        loginConfig = { ...baseConfig, ...(this._idp && { idp: this._idp }) };
      }

      const r = await logIn(loginConfig);
      console.log(
        `%cLogin result`,
        'background: pink; color: navy; padding; 10px;',
        r
      );
    } catch (e) {
      this._result = `Unable to log in ${String(e)} ${JSON.stringify(e)}`;
    }
  }

  onRedirectUriChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this._redirectUri = target.value;
  }
  onClientIdChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this._clientId = target.value;
  }

  onIdpChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this._idp = target.value;
  }

  onUserIdChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this._userId = target.value || undefined;
  }

  onIdpLookupUrlChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this._idpLookupUrl = target.value;
  }

  onIssuerUrlChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._issuerUrl = target.value;
  }

  onClientUidChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._clientUid = target.value;
  }

  onFallbackChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._fallback = target.value;
  }

  onPreferredChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._preferred = target.value;
  }

  onLinkUrlChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._linkUrl = target.value;
  }

  onRedirectRootChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._redirectRoot = target.value;
    console.log('redirect root', this._redirectRoot);
  }

  onRedirectNextPathChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._redirectNextPath = target.value;
    console.log('redirect next path', this._redirectNextPath);
  }

  onLaneChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._lane = target.value || undefined;
    this.clearSubscription();
  }

  onSetLane(lane?: string) {
    this._lane = lane;
    this.clearSubscription();
  }

  clearSubscription() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
      this._authState = '×';
      this._detailedAuthState = null;
      this._isSubscribed = false;
    }
  }

  onPnsEnvChanged(event: Event) {
    const target = event.target as HTMLSelectElement;
    const value = target.value as Env | 'disabled';
    this._pnsEnv = value;
    // Auto-fill topic when selecting an env
    if (value !== 'disabled') {
      this._pnsTopic = PNS_TOPIC_BY_ENV[value];
    } else {
      this._pnsTopic = '';
    }
  }

  onPnsTopicChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._pnsTopic = target.value;
  }

  private _buildPnsSub(): PnsSub | undefined {
    if (this._pnsEnv === 'disabled' || !this._pnsTopic) return undefined;
    return { e: this._pnsEnv, t: this._pnsTopic };
  }

  render() {
    return html`
      <div class="app">
        <h2>${this._authState}${this._lane ? ` (${this._lane})` : ''}</h2>
        ${this._detailedAuthState
          ? html`<pre
              style="background:#222;color:#9fe;padding:8px;border-radius:4px;font-size:12px;white-space:pre-wrap;"
            >
authState: ${this._detailedAuthState.authState}
reason:    ${this._detailedAuthState.reason === undefined
                ? '(none)'
                : JSON.stringify(this._detailedAuthState.reason, null, 2)}</pre
            >`
          : ''}
        <p>${globalThis.document.location.href}</p>
        <p>${this._result}</p>
        <ul>
          <li>
            <label>
              <input
                type="checkbox"
                @change=${this.onSubscriptionRequestChanged}
                .checked=${this._isSubscribed}
              />
              Subscribe to auth state stream
            </label>
          </li>
          <li>
            <button @click=${() => logOut({ lane: this._lane })}>Logout</button>
            ∷
            <button @click=${() => optOut({ lane: this._lane })}>
              Opt out
            </button>
          </li>
        </ul>
        <p>-----</p>
        <ul>
          <li>
            <label>Lane</label>
            <input
              type="text"
              @change=${this.onLaneChanged}
              .value=${this._lane || ''}
              placeholder="lane or empty for 'base'"
            />
            <button @click=${() => this.onSetLane('AAAA')}>AAAA</button>
            <button @click=${() => this.onSetLane('BBBB')}>BBBB</button>
            <button @click=${() => this.onSetLane(undefined)}>Clear</button>
          </li>
          <li>
            <label>Presets:</label>
            ${Object.keys(presets).map((preset) => {
              return html`<button
                @click=${() =>
                  this.onPresetSelected(preset as keyof typeof presets)}
              >
                ${preset}
              </button>`;
            })}
          </li>
          <li>
            <label>Client uid</label>
            <input
              type="text"
              @change=${this.onClientUidChanged}
              .value=${this._clientUid}
              placeholder="client uid"
            />
          </li>
          <li>
            <label>Issuer URL</label>
            <input
              type="text"
              .value=${this._issuerUrl || ''}
              @change=${this.onIssuerUrlChanged}
              placeholder="issuer url"
            />
          </li>
          <li>
            <label>Redirect uri</label>
            <input
              type="text"
              @change=${this.onRedirectUriChange}
              .value=${this._redirectUri || ''}
              placeholder="redirect uri"
            />
          </li>
          <li>
            <label>Client id</label>
            <input
              type="text"
              @change=${this.onClientIdChange}
              .value=${this._clientId || ''}
              placeholder="client id"
            />
          </li>
          <li>
            <label>IDP</label>
            <input
              type="text"
              @change=${this.onIdpChange}
              .value=${this._idp || ''}
              placeholder="idp (e.g., ping)"
            />
          </li>
          <li>
            <label>
              Preferred strategy
              <input
                type="text"
                @change=${this.onPreferredChanged}
                .value=${this._preferred}
                placeholder="empty, 'none' or 'popup'"
              />
            </label>
          </li>
          <li>
            <label>Fallback strategy</label>
            <input
              type="text"
              @change=${this.onFallbackChanged}
              .value=${this._fallback}
              placeholder="empty, 'none' or 'popup'"
            />
          </li>
          <li>
            <label>Link URL (for popup/silent)</label>
            <input
              type="text"
              @change=${this.onLinkUrlChanged}
              .value=${this._linkUrl}
              placeholder="link page URL"
            />
          </li>
          <li>
            <label>User ID</label>
            <input
              type="text"
              @change=${this.onUserIdChange}
              .value=${this._userId || ''}
              placeholder="user@lseg.com"
            />
          </li>
          <li>
            <label>IDP Lookup URL</label>
            <input
              type="text"
              @change=${this.onIdpLookupUrlChange}
              .value=${this._idpLookupUrl}
              placeholder="https://idp-lookup-origin"
            />
          </li>
          <li>
            <label>
              <input
                type="checkbox"
                @change=${this.onRemoteLoginChanged}
                .checked=${this._remoteLogin}
              />
              Remote Login
            </label>
          </li>

          <li>
            <label>
              <input
                type="checkbox"
                @change=${this.onPersistTokensChanged}
                .checked=${this._persistTokens}
              />
              Persist Heimdall Tokens
            </label>
          </li>
          <li>
            <label>
              <input
                type="checkbox"
                @change=${this.onOptimisticChanged}
                .checked=${this._optimistic}
              />
              Optimistic login
              ${this._optimistic === undefined
                ? '(auto -- defaults to true)'
                : `explicit ${this._optimistic}`}
            </label>
          </li>
          <li>
            <label>PNS env</label>
            <select @change=${this.onPnsEnvChanged} .value=${this._pnsEnv}>
              <option value="disabled">disabled</option>
              ${ENVS.map(
                (e) =>
                  html`<option value=${e} ?selected=${this._pnsEnv === e}>
                    ${e}
                  </option>`
              )}
            </select>
            <label>PNS topic</label>
            <input
              type="text"
              @change=${this.onPnsTopicChanged}
              .value=${this._pnsTopic}
              placeholder="topic name"
              .disabled=${this._pnsEnv === 'disabled'}
            />
          </li>
          <li>
            <label>
              Redirect root:
              <input
                type="text"
                @change=${this.onRedirectRootChanged}
                .value=${this._redirectRoot || ''}
                placeholder="where to redirect from (or empty, from here)"
              />
              Redirect next path:
              <input
                type="text"
                @change=${this.onRedirectNextPathChanged}
                .value=${this._redirectNextPath || ''}
                placeholder="where to redirect to (or empty, to here)"
              />
            </label>
          </li>
          <li>
            <button @click=${this.onLogin}>Login</button>
            <button @click=${this.onRedirectLogin}>
              Simple redirect login
            </button>
            <button @click=${this.onCopyRedirectLoginUrl} title="Copy URL">
              Copy redirection url
            </button>
          </li>
        </ul>
      </div>
    `;
  }

  static styles = rigSharedStyles;
}

declare global {
  interface HTMLElementTagNameMap {
    'heimdall-rig-auth-state': HeimdallRigAuthState;
  }
}
